/* eslint-disable import/no-named-as-default */

import React, { useEffect, useState } from 'react';

import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import QRCode from 'react-qr-code';
import { useQuery } from 'react-query';

import getUserChat from '@/api/chatApi/getUserChat';
import { editFileSettinsAPI } from '@/api/editFileSettings';
import { QrPopup } from '@/components/Popup/QrPopup';
import useMediaQuery from '@/hooks/useMediaQuery';
import useQrVisible from '@/hooks/useQrVisible';
import { instantiateGun } from '@/utils/instantiateGun';
import { IChatMessage } from 'interfaces/ChatTab/chatTab';
import { IPreviewSideBarProps } from 'interfaces/PreviewScreen/previewScreen';

import FileListing from '../IpfsHashList/FileListing';
import FileNavMenu from './FileNavMenu/FileNavMenu';
import AboutFile from './FileNavTabs/AboutFileTab';
import ChatTab from './FileNavTabs/ChatTab/ChatTab';
import FileTab from './FileNavTabs/FileTab';
import MoreTab from './FileNavTabs/MoreTab';
import SettingTab from './FileNavTabs/SettingTab';

const gun = instantiateGun();
const chatIdList = new Map(); // keep track of chat ids that has been rendered from GunDB - Gun can call a single chat multiple times, this is by design.

const PreviewSideBar = ({
  isActive,
  file,
  setIsLexical,
  isEditClicked,
  setIsEditClicked,
  isLexical,
  getFile,
  tabQueryParameter,
  setErrorPage,
  isIpfsPreview,
  allFile,
  getIpfsFiles,
  getFIlePreviewLink,
  goBackToFile,
  nestedFolder,
  filePreviewURL,
  isIpfsPreviewLoading,
  ipfsPreviewHash,
  fileErrorMessage,
}: IPreviewSideBarProps) => {
  const isMediaMax1025px = useMediaQuery('(max-width : 1025px)');
  const [active, setActive] = useState(isActive);
  const [userMessages, setUserMessages] = useState<IChatMessage[]>([]);
  const [lastMessageRecievedAt, setLastMessageRecievedAt] = useState(-1);
  const [signalNewMessage, setSignalNewMessage] = useState(false);
  const { qrRef, isQrVisible, setIsQrVisible } = useQrVisible(false);
  const [isEmbedFile, setIsEmbedFile] = useState<boolean>(false);
  const [downloadable, setDownloadable] = useState<boolean>(
    !file?.data.settings || file?.data.settings?.downloadable
  );
  const { executeRecaptcha } = useGoogleReCaptcha();

  const uuid = file?.data.uuid;
  const fileName = file?.data.name;
  const ipfsHash = file ? file?.data?.ipfsHash : ipfsPreviewHash;
  const canUserEdit = file?.data.currentPermission.edit;
  const changeDownloadOption = async () => {
    try {
      const recaptchaToken = await executeRecaptcha('edit');
      await editFileSettinsAPI(file?.data.uuid, downloadable, recaptchaToken);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!isIpfsPreview) changeDownloadOption();
  }, [downloadable]);

  const QR = () => {
    return (
      <div ref={qrRef} className="relative">
        <div className="flex-col w-full h-full items-center justify-center">
          <div className="flex w-full p-2 my-3 justify-center">
            <div className="border-2 p-2 rounded-md">
              <QRCode
                size={115}
                value={
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/files/${file?.data.uuid}` ||
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/`
                }
              />
            </div>
          </div>
          <div className="flex w-full p-2 justify-center">
            Scan QR Code from Mobile
          </div>
        </div>
      </div>
    );
  };
  const { isLoading } = useQuery(
    'getUserChats',
    () => {
      return getUserChat(uuid);
    },
    {
      onSuccess: (data: any) => {
        if (data === userMessages) {
          return;
        }
        const lastMessageTime = data[data.length - 1]?.createdAt;
        const lastMessageTrack = lastMessageRecievedAt;
        if (lastMessageRecievedAt !== lastMessageTime) {
          if (lastMessageTrack !== -1) {
            setSignalNewMessage(true);
          }
          setLastMessageRecievedAt(lastMessageTime);
          setUserMessages(data);
        }
      },
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      enabled: !isIpfsPreview,
    }
  );
  useEffect(() => {
    const validParameters = ['about', 'chat', 'setting', 'more'];
    if (tabQueryParameter && validParameters.includes(tabQueryParameter)) {
      const formatParameter = `${tabQueryParameter[0].toUpperCase()}${tabQueryParameter.substring(
        1
      )}`;
      switch (tabQueryParameter) {
        case 'more': {
          if (!canUserEdit) {
            setActive(formatParameter);
          } else {
            setActive(isActive);
          }
          break;
        }
        case 'setting': {
          if (canUserEdit) {
            setActive(formatParameter);
          } else {
            setActive(isActive);
          }
          break;
        }
        default: {
          setActive(formatParameter);
        }
      }
    } else {
      setActive(isActive);
    }
  }, [tabQueryParameter]);

  useEffect(() => {
    if (file?.data.mimetype === 'application/json') {
      axios
        .get(file.data.url)
        .then((res) => {
          setIsEmbedFile(
            res.data.source === 'Link' || res.data.source === 'Embed'
          );
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [file]);
  useEffect(() => {
    if (ipfsPreviewHash) {
      const chatCollection = gun.get(`#${ipfsPreviewHash}`);
      chatCollection.map().on((message: any, id: string) => {
        if (chatIdList.get(id) === undefined) {
          chatIdList.set(id, true);
          const data = JSON.parse(message);
          setUserMessages((prev: Array<IChatMessage>) => {
            const chat = [
              ...prev,
              {
                by: data.by,
                text: data.text,
                createdAt: data.createdAt,
              },
            ].sort((a, b) => a.createdAt - b.createdAt);
            return chat;
          });
        }
      });
    }
  });
  return (
    <div className={`rounded-[6px] h-[100%] w-full`}>
      <FileNavMenu
        isMediaMax1025px={isMediaMax1025px}
        setActive={setActive}
        active={active}
        canUserEdit={isIpfsPreview ? null : canUserEdit}
        signalNewMessage={signalNewMessage}
        setSignalNewMessage={setSignalNewMessage}
        userMessages={userMessages}
      />
      <div
        className={`${isMediaMax1025px ? 'h-[67vh]' : 'tab-h-d'} mt-2 ${
          !isIpfsPreview ? 'p-4' : ''
        }  bg-white flex overflow-y-auto no-scrollbar flex-col rounded-md`}
      >
        {active === 'About' && (
          <AboutFile
            file={isIpfsPreview ? null : file}
            fileName={isIpfsPreview ? ipfsHash : fileName}
            ipfsHash={ipfsHash}
            setIsQrVisible={isIpfsPreview ? null : setIsQrVisible}
            canUserEdit={
              isIpfsPreview ? null : file.data.currentPermission.edit
            }
            uuid={isIpfsPreview ? null : uuid}
            downloadable={isIpfsPreview ? null : downloadable}
            isEmbedFile={isIpfsPreview ? null : isEmbedFile}
            isIpfsPreview={isIpfsPreview}
          />
        )}
        {active === 'Chat' && (
          <ChatTab
            isMediaMax1025px={isMediaMax1025px}
            userMessages={userMessages}
            isLoading={isLoading}
            setSignalNewMessage={setSignalNewMessage}
            setUserMessages={setUserMessages}
            uuId={uuid}
            ipfsPreviewHash={ipfsPreviewHash}
          />
        )}
        {active === 'File' && !isIpfsPreview && (
          <FileTab
            fileResponse={file}
            setIsLexical={setIsLexical}
            isEditClicked={isEditClicked}
            setIsEditClicked={setIsEditClicked}
            isLexical={isLexical}
            getFile={getFile}
            setErrorPage={setErrorPage}
          />
        )}
        {active === 'File' && isIpfsPreview && (
          <div className="h-[76vh] relative w-full bg-white rounded-[8px] box-border">
            <FileListing
              file={allFile}
              getFiles={getIpfsFiles}
              getFIlePreviewLink={getFIlePreviewLink}
              goBackToFile={goBackToFile}
              nestedFolder={nestedFolder}
              filePreviewURL={filePreviewURL}
              isLoading={isIpfsPreviewLoading}
              fileErrorMessage={fileErrorMessage}
            />
          </div>
        )}
        {active === 'Setting' && !isIpfsPreview && (
          <SettingTab
            file={file}
            setIsEditClicked={setIsEditClicked}
            setActive={setActive}
            downloadable={downloadable}
            setDownloadable={setDownloadable}
          />
        )}
        {active === 'More' && <MoreTab />}
        <QrPopup
          isOpen={isQrVisible}
          width={isMediaMax1025px ? '90%' : '370px'}
          margin={isMediaMax1025px ? '5%' : '10% auto'}
          closePopup={() => {}}
        >
          <div className="overflow-auto max-h-full max-w-full box-border">
            {QR()}
          </div>
        </QrPopup>
      </div>
    </div>
  );
};

export default PreviewSideBar;
